/**
 * Admin page — only reachable when `me.is_admin` is true.
 *
 * Single page with four sections accessible via segmented control:
 *  - Обзор (Overview)
 *  - Пользователи (Users with search, detail sheet, block/unblock, grant plan)
 *  - Промокоды (Promos CRUD)
 *  - Рассылка (Broadcast composer + history)
 *
 * Mirrors the premium dark UI used elsewhere (glass, gradient accent).
 *
 * Route: /app/admin
 */

import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Ban,
  CalendarClock,
  Check,
  Copy,
  Crown,
  DollarSign,
  Megaphone,
  Plus,
  Search,
  Send,
  Shield,
  ShieldOff,
  Sparkles,
  Tag,
  Trash2,
  TrendingUp,
  Users as UsersIcon,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card, Input, Sheet } from "@/design";
import {
  useAdminBroadcasts,
  useAdminOverview,
  useAdminPromos,
  useAdminUser,
  useAdminUsers,
  useBlockUser,
  useCreateBroadcast,
  useCreatePromo,
  useDeletePromo,
  useGrantPlan,
  useUnblockUser,
  type AdminBroadcast,
  type AdminPromo,
  type AdminUserRow,
  type BroadcastParseMode,
  type BroadcastSegment,
} from "@/api/admin";
import { useMe } from "@/api/me";
import { usePlans } from "@/api/billing";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

type Tab = "overview" | "users" | "promos" | "broadcast";

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Обзор" },
  { id: "users", label: "Юзеры" },
  { id: "promos", label: "Промо" },
  { id: "broadcast", label: "Рассылка" },
];

export function AdminPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [tab, setTab] = useState<Tab>("overview");

  // Soft access guard. Backend enforces, but this prevents flashing UI.
  useEffect(() => {
    if (me && !me.is_admin) navigate("/app", { replace: true });
  }, [me, navigate]);

  if (!me?.is_admin) {
    return (
      <div className="px-5 pt-10 text-center text-text-muted">
        Доступ только для администраторов.
      </div>
    );
  }

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight flex items-center gap-1.5">
          <Shield className="size-4 text-accent" />
          Админ-панель
        </div>
        <div className="size-10" />
      </div>

      {/* Segmented tabs */}
      <div className="relative grid grid-cols-4 gap-1 p-1 rounded-2xl glass">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              haptic.select();
              setTab(t.id);
            }}
            className={cn(
              "relative h-9 rounded-xl text-[12px] font-medium transition-colors",
              tab === t.id ? "text-text" : "text-text-muted hover:text-text",
            )}
          >
            {tab === t.id && (
              <motion.div
                layoutId="admin-tab"
                className="absolute inset-0 rounded-xl bg-white/[0.07] border border-white/[0.06]"
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              />
            )}
            <span className="relative">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {tab === "overview" && <OverviewPanel />}
          {tab === "users" && <UsersPanel />}
          {tab === "promos" && <PromosPanel />}
          {tab === "broadcast" && <BroadcastPanel />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// Overview
// ============================================================

function OverviewPanel() {
  const { data, isLoading } = useAdminOverview();

  if (isLoading || !data) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
        Загрузка…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card padded={false} className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-transparent pointer-events-none"
          aria-hidden
        />
        <div className="relative p-6 space-y-2">
          <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
            Выручка
          </div>
          <div className="text-[36px] leading-none font-semibold tabular-nums tracking-tight">
            ${data.revenue_usd.toFixed(2)}
          </div>
          <div className="text-[12px] text-text-muted flex items-center gap-1.5">
            <TrendingUp className="size-3" />
            {data.paid_users} платящих пользователей
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-2.5">
        <MetricTile
          icon={<UsersIcon className="size-3.5" />}
          label="Всего"
          value={data.total_users}
          unit="юзеров"
        />
        <MetricTile
          icon={<Sparkles className="size-3.5" />}
          label="Сегодня"
          value={data.new_users_today}
          unit="новых"
        />
        <MetricTile
          icon={<Crown className="size-3.5" />}
          label="Активные"
          value={data.active_subs}
          unit="подписок"
        />
        <MetricTile
          icon={<Ban className="size-3.5" />}
          label="Блок"
          value={data.blocked_users}
          unit="юзеров"
          tone="danger"
        />
        <MetricTile
          icon={<DollarSign className="size-3.5" />}
          label="Выплаты"
          value={data.pending_payouts}
          unit="ожидают"
          tone={data.pending_payouts > 0 ? "warn" : "default"}
        />
      </div>
    </div>
  );
}

function MetricTile({
  icon,
  label,
  value,
  unit,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  unit: string;
  tone?: "default" | "danger" | "warn";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-white/[0.02] p-3 space-y-2",
        tone === "danger" && "border-danger/30",
        tone === "warn" && "border-warning/30",
        tone === "default" && "border-white/[0.06]",
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "size-6 rounded-lg grid place-items-center",
            tone === "danger" && "bg-danger/15 text-danger",
            tone === "warn" && "bg-warning/15 text-warning",
            tone === "default" && "bg-white/[0.04] text-text-dim",
          )}
        >
          {icon}
        </div>
        <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
          {label}
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-[22px] leading-none font-semibold tabular-nums tracking-tight">
          {value}
        </span>
        <span className="text-[10px] text-text-muted">{unit}</span>
      </div>
    </div>
  );
}

// ============================================================
// Users
// ============================================================

function UsersPanel() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [openUserId, setOpenUserId] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading } = useAdminUsers({ q: debouncedQ || undefined, limit: 50 });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="size-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-text-dim pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ID, username или имя"
          className="w-full h-11 rounded-2xl bg-white/[0.04] border border-white/[0.06] pl-10 pr-3 text-[14px] placeholder:text-text-dim outline-none focus:border-accent/40"
        />
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
          Загрузка…
        </div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
          Никого не найдено
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <>
          <div className="text-[11px] text-text-dim px-1">
            Найдено: <span className="text-text-muted">{data.total}</span>
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
            {data.items.map((u) => (
              <UserRow key={u.id} user={u} onOpen={() => setOpenUserId(u.id)} />
            ))}
          </div>
        </>
      )}

      <UserDetailSheet
        userId={openUserId}
        open={openUserId != null}
        onClose={() => setOpenUserId(null)}
      />
    </div>
  );
}

function UserRow({ user, onOpen }: { user: AdminUserRow; onOpen: () => void }) {
  return (
    <button
      onClick={() => {
        haptic.select();
        onOpen();
      }}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors active:scale-[0.998]"
    >
      <div
        className={cn(
          "size-9 rounded-xl grid place-items-center text-[12px] font-semibold shrink-0",
          user.is_blocked
            ? "bg-danger/15 text-danger"
            : user.plan_id
            ? "bg-accent-soft text-accent"
            : "bg-white/[0.06] text-text-dim",
        )}
      >
        {user.first_name?.[0]?.toUpperCase() ?? user.username?.[0]?.toUpperCase() ?? "?"}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-[14px] font-medium truncate flex items-center gap-1.5">
          {user.first_name ?? user.username ?? `User ${user.id}`}
          {user.is_blocked && <Ban className="size-3 text-danger shrink-0" />}
        </div>
        <div className="text-[11px] text-text-muted truncate mt-0.5">
          {user.username ? `@${user.username} · ` : ""}ID {user.id}
          {user.plan_id ? ` · ${user.plan_id}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[12px] font-semibold tabular-nums">
          ${user.total_paid_usd.toFixed(0)}
        </div>
        <div className="text-[10px] text-text-dim">
          {user.total_sent_messages.toLocaleString("ru-RU")} отпр.
        </div>
      </div>
    </button>
  );
}

function UserDetailSheet({
  userId,
  open,
  onClose,
}: {
  userId: number | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: user } = useAdminUser(userId);
  const { data: plans } = usePlans();
  const block = useBlockUser();
  const unblock = useUnblockUser();
  const grant = useGrantPlan();
  const [grantPlanId, setGrantPlanId] = useState<string | null>(null);
  const [grantDays, setGrantDays] = useState("");

  const onToggleBlock = async () => {
    if (!user) return;
    try {
      if (user.is_blocked) {
        await unblock.mutateAsync({ userId: user.id });
        toast.success("Разблокирован");
      } else {
        await block.mutateAsync({ userId: user.id });
        toast.success("Заблокирован");
      }
      haptic.notify("success");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка");
      haptic.notify("error");
    }
  };

  const onGrant = async () => {
    if (!user || !grantPlanId) return;
    try {
      const days = grantDays.trim() ? Number(grantDays.trim()) : null;
      if (days != null && (!Number.isFinite(days) || days <= 0)) {
        toast.error("Некорректное число дней");
        return;
      }
      await grant.mutateAsync({
        userId: user.id,
        plan_id: grantPlanId,
        duration_days: days,
      });
      toast.success("План выдан");
      haptic.notify("success");
      setGrantPlanId(null);
      setGrantDays("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка");
      haptic.notify("error");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={user ? user.first_name ?? user.username ?? `User ${user.id}` : "Пользователь"}
      description={user ? `ID ${user.id}${user.username ? ` · @${user.username}` : ""}` : undefined}
    >
      {!user ? (
        <div className="py-10 text-center text-xs text-text-muted">Загрузка…</div>
      ) : (
        <div className="space-y-5">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-2">
            <KV label="План" value={user.plan_id ?? "—"} />
            <KV
              label="Истекает"
              value={formatDate(user.plan_expires_at) ?? "—"}
            />
            <KV label="Оплачено" value={`$${user.total_paid_usd.toFixed(2)}`} />
            <KV label="Отправлено" value={user.total_sent_messages.toLocaleString("ru-RU")} />
            <KV label="Чатов" value={user.chats_count} />
            <KV label="Аккаунтов" value={user.accounts_count} />
            <KV label="Рефералы" value={user.referrals_count} />
            <KV label="Баланс реф." value={`$${user.referral_balance_usd.toFixed(2)}`} />
          </div>

          {/* Block / unblock */}
          <Button
            fullWidth
            variant={user.is_blocked ? "secondary" : "danger"}
            onClick={onToggleBlock}
            loading={block.isPending || unblock.isPending}
          >
            {user.is_blocked ? (
              <>
                <ShieldOff className="size-4" /> Разблокировать
              </>
            ) : (
              <>
                <Ban className="size-4" /> Заблокировать
              </>
            )}
          </Button>

          {/* Grant plan */}
          <div className="space-y-2">
            <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
              Выдать план
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(plans ?? []).map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    haptic.select();
                    setGrantPlanId(p.id);
                  }}
                  className={cn(
                    "h-11 rounded-xl text-[12px] font-medium transition-colors border",
                    grantPlanId === p.id
                      ? "border-accent/50 bg-accent/15 text-accent"
                      : "border-white/[0.06] bg-white/[0.02] text-text-muted hover:bg-white/[0.04]",
                  )}
                >
                  {p.title}
                </button>
              ))}
            </div>
            <Input
              placeholder={
                grantPlanId
                  ? `Дни (по умолч. ${plans?.find((p) => p.id === grantPlanId)?.duration_days ?? "—"})`
                  : "Дни (опционально)"
              }
              type="number"
              inputMode="numeric"
              value={grantDays}
              onChange={(e) => setGrantDays(e.target.value)}
            />
            <Button
              fullWidth
              onClick={onGrant}
              loading={grant.isPending}
              disabled={!grantPlanId}
            >
              Выдать
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

function KV({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[10px] text-text-dim uppercase tracking-wider">{label}</div>
      <div className="text-[13px] font-semibold tabular-nums truncate mt-0.5">{value}</div>
    </div>
  );
}

// ============================================================
// Promos
// ============================================================

function PromosPanel() {
  const { data: promos, isLoading } = useAdminPromos();
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-3">
      <Button fullWidth variant="primary" onClick={() => setOpen(true)}>
        <Plus className="size-4" />
        Создать промокод
      </Button>

      {isLoading && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
          Загрузка…
        </div>
      )}

      {!isLoading && promos && promos.length === 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center text-xs text-text-muted">
          Промокодов пока нет
        </div>
      )}

      {!isLoading && promos && promos.length > 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {promos.map((p) => (
            <PromoRow key={p.id} promo={p} />
          ))}
        </div>
      )}

      <PromoCreateSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function PromoRow({ promo }: { promo: AdminPromo }) {
  const del = useDeletePromo();
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      toast.success("Скопировано");
      haptic.notify("success");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };
  const onDelete = async () => {
    if (!confirm(`Удалить промокод ${promo.code}?`)) return;
    try {
      await del.mutateAsync({ promoId: promo.id });
      toast.success("Удалён");
      haptic.notify("success");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка");
    }
  };
  const exhausted = promo.used_count >= promo.max_uses;
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div
        className={cn(
          "size-9 rounded-xl grid place-items-center shrink-0",
          exhausted ? "bg-white/[0.06] text-text-dim" : "bg-accent-soft text-accent",
        )}
      >
        <Tag className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium font-mono truncate">{promo.code}</div>
        <div className="text-[11px] text-text-muted truncate mt-0.5">
          План <span className="text-text">{promo.plan_id}</span> · {promo.used_count}/{promo.max_uses}
        </div>
      </div>
      <button
        onClick={onCopy}
        className="size-9 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] grid place-items-center transition-colors"
        aria-label="Копировать"
      >
        <Copy className="size-3.5 text-text-dim" />
      </button>
      <button
        onClick={onDelete}
        className="size-9 rounded-xl bg-white/[0.04] hover:bg-danger/15 hover:text-danger grid place-items-center transition-colors"
        aria-label="Удалить"
      >
        <Trash2 className="size-3.5 text-text-dim" />
      </button>
    </div>
  );
}

function PromoCreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: plans } = usePlans();
  const create = useCreatePromo();
  const [code, setCode] = useState("");
  const [planId, setPlanId] = useState<string | null>(null);
  const [maxUses, setMaxUses] = useState("1");

  useEffect(() => {
    if (!open) {
      setCode("");
      setPlanId(null);
      setMaxUses("1");
    }
  }, [open]);

  const submit = async () => {
    if (!code.trim() || !planId) return;
    const max = Number(maxUses);
    if (!Number.isFinite(max) || max < 1) {
      toast.error("Некорректное число использований");
      return;
    }
    try {
      await create.mutateAsync({ code: code.trim().toUpperCase(), plan_id: planId, max_uses: max });
      toast.success("Промокод создан");
      haptic.notify("success");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка");
      haptic.notify("error");
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Новый промокод">
      <div className="space-y-4">
        <Input
          placeholder="КОД"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          autoFocus
        />
        <div className="space-y-2">
          <div className="text-[11px] text-text-dim uppercase tracking-wider">План</div>
          <div className="grid grid-cols-3 gap-2">
            {(plans ?? []).map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  haptic.select();
                  setPlanId(p.id);
                }}
                className={cn(
                  "h-11 rounded-xl text-[12px] font-medium transition-colors border",
                  planId === p.id
                    ? "border-accent/50 bg-accent/15 text-accent"
                    : "border-white/[0.06] bg-white/[0.02] text-text-muted hover:bg-white/[0.04]",
                )}
              >
                {p.title}
              </button>
            ))}
          </div>
        </div>
        <Input
          placeholder="Лимит активаций"
          type="number"
          inputMode="numeric"
          value={maxUses}
          onChange={(e) => setMaxUses(e.target.value)}
        />
        <Button
          fullWidth
          onClick={submit}
          loading={create.isPending}
          disabled={!code.trim() || !planId}
        >
          Создать
        </Button>
      </div>
    </Sheet>
  );
}

// ============================================================
// Broadcast
// ============================================================

const SEGMENTS: { id: BroadcastSegment; label: string; desc: string }[] = [
  { id: "all", label: "Все", desc: "Все не заблокированные" },
  { id: "active", label: "Активные", desc: "С активной подпиской" },
  { id: "free", label: "Без плана", desc: "Без активной подписки" },
  { id: "paid", label: "Платившие", desc: "Хотя бы раз платили" },
];

const PARSE_MODES: { id: BroadcastParseMode; label: string }[] = [
  { id: "HTML", label: "HTML" },
  { id: "MarkdownV2", label: "Markdown" },
  { id: "None", label: "Plain" },
];

function BroadcastPanel() {
  const create = useCreateBroadcast();
  const { data } = useAdminBroadcasts();
  const [text, setText] = useState("");
  const [segment, setSegment] = useState<BroadcastSegment>("all");
  const [parseMode, setParseMode] = useState<BroadcastParseMode>("HTML");

  const submit = async () => {
    if (!text.trim()) return;
    if (!confirm(`Запустить рассылку для сегмента "${segment}"?`)) return;
    try {
      await create.mutateAsync({ text: text.trim(), segment, parse_mode: parseMode });
      toast.success("Рассылка запущена");
      haptic.notify("success");
      setText("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Ошибка");
      haptic.notify("error");
    }
  };

  const recent = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div>
          <div className="text-[11px] text-text-dim uppercase tracking-wider mb-2">Сегмент</div>
          <div className="grid grid-cols-2 gap-2">
            {SEGMENTS.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  haptic.select();
                  setSegment(s.id);
                }}
                className={cn(
                  "rounded-xl text-left p-3 transition-colors border",
                  segment === s.id
                    ? "border-accent/50 bg-accent/15"
                    : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]",
                )}
              >
                <div
                  className={cn(
                    "text-[13px] font-semibold",
                    segment === s.id ? "text-accent" : "text-text",
                  )}
                >
                  {s.label}
                </div>
                <div className="text-[10px] text-text-muted mt-0.5">{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-text-dim uppercase tracking-wider mb-2">Формат</div>
          <div className="grid grid-cols-3 gap-2">
            {PARSE_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  haptic.select();
                  setParseMode(m.id);
                }}
                className={cn(
                  "h-9 rounded-xl text-[12px] font-medium transition-colors border",
                  parseMode === m.id
                    ? "border-accent/50 bg-accent/15 text-accent"
                    : "border-white/[0.06] bg-white/[0.02] text-text-muted hover:bg-white/[0.04]",
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-text-dim uppercase tracking-wider mb-2 flex items-center justify-between">
            <span>Сообщение</span>
            <span className="tabular-nums">{text.length}/4096</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 4096))}
            placeholder="Текст рассылки…"
            rows={6}
            className="w-full rounded-2xl bg-white/[0.04] border border-white/[0.06] px-4 py-3 text-[14px] placeholder:text-text-dim outline-none focus:border-accent/40 resize-none"
          />
        </div>

        <Button
          fullWidth
          variant="primary"
          onClick={submit}
          loading={create.isPending}
          disabled={!text.trim()}
        >
          <Send className="size-4" />
          Запустить рассылку
        </Button>
      </Card>

      {recent.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
            <Megaphone className="size-3" />
            Последние рассылки
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
            {recent.map((b) => (
              <BroadcastRow key={b.id} b={b} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BroadcastRow({ b }: { b: AdminBroadcast }) {
  const pct = b.total > 0 ? Math.min(100, Math.round(((b.sent + b.failed) / b.total) * 100)) : 0;
  const statusColor =
    b.status === "done"
      ? "text-success"
      : b.status === "running"
      ? "text-accent"
      : b.status === "failed"
      ? "text-danger"
      : "text-text-muted";
  const statusLabel =
    b.status === "done"
      ? "Завершена"
      : b.status === "running"
      ? "Идёт"
      : b.status === "failed"
      ? "Ошибка"
      : "В очереди";
  return (
    <div className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] truncate flex-1">{b.text_preview || "—"}</div>
        <div className={cn("text-[11px] font-medium uppercase tracking-wider shrink-0", statusColor)}>
          {statusLabel}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-text-muted">
        <span className="capitalize">{b.segment}</span>
        <span>·</span>
        <span className="flex items-center gap-1">
          <Check className="size-3 text-success" />
          {b.sent}
        </span>
        <span className="flex items-center gap-1">
          <X className="size-3 text-danger" />
          {b.failed}
        </span>
        <span className="ml-auto tabular-nums">
          {b.sent + b.failed}/{b.total}
        </span>
      </div>
      {b.total > 0 && (
        <div className="h-1 rounded-full bg-white/[0.04] overflow-hidden">
          <div
            className="h-full bg-gradient-accent rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="text-[10px] text-text-dim flex items-center gap-1">
        <CalendarClock className="size-3" />
        {formatDate(b.created_at)}
      </div>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
